import React, { createContext, useState, useEffect, useContext } from 'react';
import { getConfig } from '../config';
import apiServicewt from '../api/apiServicewt';
import { useLanguageSwitch } from '../contexts/LanguageSwitchContext';

const LoginSettingsContext = createContext();

export const LoginSettingsProvider = ({ children }) => {
	const [loginSettings, setLoginSettings] = useState({});
	const [loginSettingsLang, setLoginSettingsLang] = useState({});
	const [loading, setLoading] = useState(true);
	const { setIsLangSwitchEnabled } = useLanguageSwitch();
	const PUBLIC_URL = process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '';

	useEffect(() => {
		try {
			const saved = localStorage.getItem('app_favicon_url');
			removeAllFavicons();
			if (saved && saved.trim()) {
				applyFavicon(saved);
			} else {
				applyFavicon(`${PUBLIC_URL}/favicon.png`);
			}
		} catch (e) {
			removeAllFavicons();
			applyFavicon(`${PUBLIC_URL}/favicon.png`);
		}
	}, []);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const item = { action: 'get_login_settings' };
				const flechdata = await apiServicewt(item);
				if (flechdata.type === 'Success' || flechdata.type === 'success') {
					const merged = {
						...(flechdata.data || {}),
						...(flechdata.permission_settings
							? { permission_settings: flechdata.permission_settings }
							: {}),
					};
					setLoginSettings(merged);
				}
			} catch (err) {
				console.error('Failed to fetch login settings:', err);
			} finally {
				setLoading(false);
			}
		};

		const fetchSettingsLang = async () => {
			try {
				const item = { action: 'get_settings' };
				const flechdata = await apiServicewt(item);
				if (flechdata.type === 'Success' || flechdata.type === 'success') {
					setLoginSettingsLang(flechdata.data || {});
					setIsLangSwitchEnabled(flechdata.data.userweb_lang_switch === '1');
				}
			} catch (err) {
				console.error('Failed to fetch login settings lang:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchSettings();
		fetchSettingsLang();
	}, []);

	useEffect(() => {
		if (loading) return;
		const candidateKeys = ['fav_icon'];
		const iconUrl = candidateKeys
			.map((k) => loginSettings?.[k])
			.find((v) => typeof v === 'string' && v.trim().length > 0);
		removeAllFavicons();
		if (iconUrl) {
			applyFavicon(iconUrl);
			try {
				localStorage.setItem('app_favicon_url', iconUrl);
			} catch (e) {}
		} else {
			applyFavicon(`${PUBLIC_URL}/sd.png`);
		}
	}, [loading, loginSettings]);

	const applyFavicon = (href) => {
		try {
			if (!href) return;
			const head = document.head || document.getElementsByTagName('head')[0];
			if (!head) return;
			const createLink = (rel, sizes) => {
				const link = document.createElement('link');
				link.rel = rel;
				if (sizes) link.sizes = sizes;
				const separator = href.includes('?') ? '&' : '?';
				link.href = `${href}${separator}v=${Date.now()}`;
				return link;
			};
			head.appendChild(createLink('icon'));
			head.appendChild(createLink('shortcut icon'));
			head.appendChild(createLink('apple-touch-icon', '180x180'));
		} catch (e) {
			console.warn('Failed to apply favicon:', e);
		}
	};

	const removeAllFavicons = () => {
		const head = document.head || document.getElementsByTagName('head')[0];
		if (!head) return;
		const faviconSelectors = [
			'link[rel="icon"]',
			'link[rel="shortcut icon"]',
			'link[rel="apple-touch-icon"]',
			'link[rel="apple-touch-icon-precomposed"]',
			'link[rel="mask-icon"]',
		];
		faviconSelectors.forEach((selector) => {
			const existingLinks = head.querySelectorAll(selector);
			existingLinks.forEach((el) => {
				if (el.parentNode) {
					el.parentNode.removeChild(el);
				}
			});
		});
	};

	return (
		<LoginSettingsContext.Provider value={{ loginSettings, loginSettingsLang, loading }}>
			{children}
		</LoginSettingsContext.Provider>
	);
};

export const useLoginSettings = () => useContext(LoginSettingsContext);
